import { POPOVER_Z_INDEX } from '@srm/ui/lib/zIndex';
import { usePortalTarget } from '@srm/ui/provider';
import { KeyboardEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
    FloatingFocusManager,
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    shift,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { MdOutlineContentCopy } from 'react-icons/md';
import Button from '@srm/ui/button';

export interface ISystemLinkDetailsProps {
    reference: RefObject<HTMLButtonElement | null>;
    title: string;
    meta: string;
    address: string;
    newTab: boolean;
    /** returnFocus — закрытие с клавиатуры: фокус возвращается на кнопку сведений. */
    onClose: (returnFocus: boolean) => void;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

/**
 * Сведения о ссылке. Монтируется только на время открытия: в таблице на тысячи строк
 * закрытый поповер в каждой ячейке держал бы позиционирование и document-слушатели.
 */
const SystemLinkDetails = ({ reference, title, meta, address, newTab, onClose }: ISystemLinkDetailsProps) => {
    const { refs, floatingStyles, context } = useFloating({
        open: true,
        onOpenChange: (open) => {
            if (!open) onClose(false);
        },
        placement: 'bottom-end',
        middleware: [offset(6), flip({ padding: 12 }), shift({ padding: 12 })],
        whileElementsMounted: autoUpdate,
        elements: { reference: reference.current },
    });
    // Escape обрабатывается в самом окне: событие не должно дойти до модалки или таблицы.
    const dismiss = useDismiss(context, { escapeKey: false });
    const role = useRole(context, { role: 'dialog' });
    const { getFloatingProps } = useInteractions([dismiss, role]);
    const portalTarget = usePortalTarget();
    const [copyState, setCopyState] = useState('');
    const copyAttempt = useRef(0);

    useEffect(
        () => () => {
            copyAttempt.current++;
        },
        [],
    );

    const copy = async () => {
        const attempt = ++copyAttempt.current;
        setCopyState('Копирование…');
        try {
            await navigator.clipboard.writeText(address);
            if (attempt === copyAttempt.current) setCopyState('Ссылка скопирована');
        } catch {
            if (attempt === copyAttempt.current)
                setCopyState('Не удалось скопировать. Выделите адрес и скопируйте вручную.');
        }
    };
    // Портал всплывает по дереву React: клавиши и клики не должны доходить до навигации
    // и выделения строк таблицы, в ячейке которой стоит ссылка.
    const onKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === 'Escape') onClose(true);
    };

    return (
        <FloatingPortal root={portalTarget}>
            <FloatingFocusManager context={context} returnFocus={false}>
                <div
                    ref={refs.setFloating}
                    className="system-link__popover"
                    style={{ ...floatingStyles, zIndex: POPOVER_Z_INDEX }}
                    aria-label={`Сведения о ссылке: ${title}`}
                    {...getFloatingProps({
                        onKeyDown,
                        onMouseDown: stop,
                        onTouchStart: stop,
                        onClick: stop,
                        onDoubleClick: stop,
                    })}
                >
                    <span className="system-link__popover-title">{title}</span>
                    <span className="system-link__meta">{meta}</span>
                    <span className="system-link__address">{address}</span>
                    <span className="system-link__navigation">
                        {newTab ? 'Откроется в новой вкладке' : 'Откроется в текущей вкладке'}
                    </span>
                    <Button
                        variant="text"
                        size="sm"
                        startAdornment={<MdOutlineContentCopy size={16} />}
                        onClick={() => void copy()}
                    >
                        Скопировать ссылку
                    </Button>
                    {copyState && (
                        <span className="system-link__copy-status" role="status">
                            {copyState}
                        </span>
                    )}
                </div>
            </FloatingFocusManager>
        </FloatingPortal>
    );
};

export default SystemLinkDetails;
